import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, UserPlus } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * T119 — public invite acceptance landing page (`/invite/:token`).
 *
 * Two flows:
 *  1. Already logged in → POST accept, no body needed → join org.
 *  2. Not logged in → show email + password form → POST accept with
 *     {signup: {email, password}} → auto-creates account + joins org.
 *
 * The token itself is sufficient capability — no separate invite-lookup
 * endpoint to avoid an enumeration oracle.
 */
export function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [needsSignup, setNeedsSignup] = useState(false);

  // Probe current auth state so we can show the right UI without needing
  // the user to type credentials they don't need.
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<{ user?: { email: string } | null }>("/auth/me").catch(() => ({ user: null })),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; orgSlug: string; role: string }>(
        `/invites/${token}/accept`,
        needsSignup ? { signup: { email, password } } : {},
      ),
    onSuccess: (r) => {
      nav(`/orgs/${r.orgSlug}`, { replace: true });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "signup_required") setNeedsSignup(true);
    },
  });

  const isLoggedIn = !!me.data?.user;

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm of-card">
        <div className="of-card-header">
          <h1 className="text-base font-semibold inline-flex items-center gap-2">
            <UserPlus className="h-4 w-4" /> You're invited
          </h1>
        </div>
        <div className="px-5 py-4 space-y-3">
          {me.isLoading ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin inline-block" />
            </div>
          ) : isLoggedIn || (!needsSignup && !accept.isError) ? (
            <>
              <p className="text-sm">
                {isLoggedIn ? (
                  <>Logged in as <span className="font-mono">{me.data!.user!.email}</span>. Click below to join the organization.</>
                ) : (
                  <>Click Accept below. If you don't have an account, you'll be asked to set one up.</>
                )}
              </p>
              <Button
                variant="primary"
                className="w-full justify-center"
                disabled={accept.isPending}
                onClick={() => accept.mutate()}
              >
                {accept.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                Accept invite
              </Button>
            </>
          ) : (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                accept.mutate();
              }}
            >
              <p className="text-xs text-muted-foreground">
                Create an account to join.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs">Password (min 8)</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <Button type="submit" variant="primary" className="w-full justify-center" disabled={!email || password.length < 8 || accept.isPending}>
                {accept.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Create account & join
              </Button>
            </form>
          )}
          {accept.error instanceof ApiError && accept.error.code !== "signup_required" && (
            <div className="text-xs text-danger">{accept.error.message}</div>
          )}
        </div>
      </div>
    </div>
  );
}
